import { LineItem, User } from '../../core';
import { SalesTermsMapper } from '../../mappers';
import {
  AccountLevel,
  BusinessEntity,
  Contact,
  CustomFields,
  Expense,
  LineItem as LineItemType,
  ListTemplate,
  Membership,
  Note,
  Organization,
  Place,
  SalesTerms,
  Shipment,
} from '../../types';
import Money from '../../utils/Money';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { BusinessEntityComponent, BusinessEntityTemplateType } from '../components/businessEntity.template';
import { documentNotes } from '../components/documentNotes.template';
import { ExpensesComponent, ExpenseTemplateDataType } from '../components/expensesGrid.template';
import { OrganizationComponent } from '../components/organization.template';
import { getProductAttributes, ProductsComponent, ProductTemplateDataType } from '../components/productsGrid.template';
import { ShipmentComponent } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';

import { applyListMeta, formatCurrency, formatDate, getPrimaryBusiness } from './formatters';

// hardcoded until material inventory is added in
const MaterialInventoryProductIDs = {
  // Millennium Pacific
  4284: 'Pallet CHEP',
};

export type PurchaseOrderOptions = {
  /**
   * Allow document type to set preferred units for total calculation
   */
  totalUnitsType: 'ordered' | 'received';
  productDescriptionTemplate?: string;
};

export type PurchaseOrderInputType = {
  organization: Organization;
  slug: string;
  receiveDate: string | Date; // on POs, the delivery date is on the order itself
  buyer: Membership;
  buyerId: number;
  businessEntity?: BusinessEntity;
  businessEntityId?: number;
  subsidiary?: Place;
  businessEntityContactId?: number;
  businessEntityContact?: Contact;
  currency?: string;
  submittedAt: string | Date;
  salesTerms: SalesTerms;
  status: string;
  currencyCode?: string;
  customFields: CustomFields;
  shipment: Shipment;
  lineItems: LineItemType[];
  expenses: Expense[];
  notes: Note[];
  vendorPo: string; // should be vendorSo
  soldThroughSalesOrder?: {
    shipment: Shipment;
    shipDate: string | Date;
    deliveryDate: string | Date;
    fulfilledByPurchaseOrders: Pick<PurchaseOrderInputType, 'shipment' | 'lineItems'>[];
    lineItems: LineItemType[];
  };
};

type PurchaseOrderTemplateDataType = Partial<{
  orderNumber: string;
  status: string;
  salesPerson: string;
  vendor: BusinessEntityTemplateType;
  shipment: Partial<{
    shipDate: string;
    deliveryDate: string;
    origin: AddressTemplateType;
    destination: AddressTemplateType;
    temperatureMin: number;
    temperatureMax: number;
    customFields: CustomFields;
  }>;
  products: ProductTemplateDataType;
  expenses: ExpenseTemplateDataType;
  customFields: CustomFields;
  billToAddress: AddressTemplateType;
  chargesSubtotal: string;
  vendorSo: string;
  notes: ListTemplate<{ text: string }>;

  phoneNumber: string;
  email: string;
  businessAddress: AddressTemplateType;
}>;

export const PurchaseOrderTemplate: DocumentTypePopulator<
  PurchaseOrderInputType,
  PurchaseOrderOptions,
  PurchaseOrderTemplateDataType
> = {
  populate: (purchaseOrder, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!purchaseOrder) {
      return {};
    }

    const {
      slug,
      organization,
      subsidiary: orderSubsidiary,
      status,
      buyer,
      shipment,
      businessEntity,
      receiveDate,
      currencyCode,
      lineItems,
      submittedAt,
      customFields,
      expenses,
      salesTerms,
      vendorPo,
      notes,
      soldThroughSalesOrder,
    } = purchaseOrder;

    const nonMaterialInventoryLineItems = lineItems.filter(
      (li) => !li?.product?.id || !(li.product.id in MaterialInventoryProductIDs)
    );

    const mergedLineItems = LineItem.Utils.flattenForDisplay(nonMaterialInventoryLineItems);

    const arExpenses = expenses.filter((expense) => {
      return expense.accountLevel === AccountLevel.ACCOUNTS_RECEIVABLE;
    });

    const apExpenses = expenses.filter((expense) => {
      const arExpense = expense.accountLevel === AccountLevel.ACCOUNTS_PAYABLE;
      const orderExpense = expense.businessEntity?.id === businessEntity?.id;

      return arExpense && orderExpense;
    });

    const freightExpenses = expenses.filter((expense) => {
      const apExpense = expense.accountLevel === AccountLevel.ACCOUNTS_PAYABLE;
      const freightExpense = expense.businessEntity?.id === shipment.freightCompany?.id;

      return apExpense && freightExpense;
    });

    const arCharges = arExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);
    const apCharges = apExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);
    const freightExpensesSubtotal = freightExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);

    const primaryBusiness = getPrimaryBusiness(organization);

    const fulfilledByPurchaseOrders = soldThroughSalesOrder?.fulfilledByPurchaseOrders;
    const salesOrderLineItems = soldThroughSalesOrder?.lineItems
      ? LineItem.Utils.flattenForDisplay(soldThroughSalesOrder.lineItems)
      : undefined;
    const orderCount = (salesOrderLineItems ? 1 : 0) + (fulfilledByPurchaseOrders?.length ?? 0);

    const pickups =
      fulfilledByPurchaseOrders?.map((po, i) => ({
        address: AddressComponent.templatize(po.shipment.place),
        products: ProductsComponent.templatize(po.lineItems, {
          descriptionTemplate: productDescriptionTemplate ?? organization.productDescriptionTemplate,
          currency: currencyCode,
        }),
        stop: i + 1,
      })) ?? [];

    if ((salesOrderLineItems?.length ?? 0) > 0) {
      pickups.push({
        address: AddressComponent.templatize(soldThroughSalesOrder?.shipment.origin),
        products: ProductsComponent.templatize(salesOrderLineItems as any, {
          descriptionTemplate: productDescriptionTemplate ?? organization.productDescriptionTemplate,
          currency: currencyCode,
        }),
        stop: pickups.length + 1,
      });
    }

    const subsidiary = orderSubsidiary ?? buyer?.subsidiary;

    return {
      orderNumber: slug,

      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({
        organization,
        subsidiary,
        customOrgName: globalOptions.customOrgName,
      }),

      subsidiary: subsidiary?.description,
      phoneNumber: primaryBusiness?.phone ?? shipment.origin?.phone,
      email: buyer?.user?.email,
      businessAddress: AddressComponent.templatize(subsidiary ?? primaryBusiness),
      fax: primaryBusiness?.faxNumber,

      status,
      salesPerson: User.formatName(buyer?.user),
      vendor: BusinessEntityComponent.templatize(businessEntity),
      salesTerms: SalesTermsMapper.getLabel(salesTerms),
      vendorSo: vendorPo,
      orderDate: formatDate(submittedAt),

      shipment: ShipmentComponent.templatize(
        soldThroughSalesOrder
          ? {
              ...shipment,
              destination: soldThroughSalesOrder.shipment.place,
              deliveryDate: soldThroughSalesOrder.deliveryDate,
              shipDate: soldThroughSalesOrder.shipDate,
              origin: shipment.place,
            }
          : { ...shipment, deliveryDate: receiveDate, origin: shipment.place }
      ),

      products: ProductsComponent.templatize(mergedLineItems as any, {
        currency: currencyCode,
        descriptionTemplate: productDescriptionTemplate ?? organization.productDescriptionTemplate,
      }),

      noOtherOrders: orderCount === 0,
      hasOtherOrders: orderCount >= 1,
      pickUps: applyListMeta(pickups),

      notes: documentNotes(notes, templateId, type),

      apCharges: ExpensesComponent.templatize(apExpenses, { currency: currencyCode }),

      customFields,

      // computed values

      freightChargesSubtotal: formatCurrency(freightExpensesSubtotal, currencyCode),
      apChargesSubtotal: formatCurrency(apCharges, currencyCode),
      ...getProductAttributes(lineItems, apCharges, arCharges),
    };
  },
  definitionMap: {
    orderNumber: { label: 'Order Number', type: 'string' },
    status: { label: 'Status', type: 'string' },
    salesPerson: { label: 'Sales Person', type: 'string' },
    vendor: {
      label: 'Vendor',
      type: 'object',
      children: BusinessEntityComponent.definitionMap({ customFieldsRoot: 'purchaseOrder' }),
    },
    shipment: {
      label: 'Shipment',
      type: 'object',
      children: ShipmentComponent.definitionMap({ customFieldsRoot: 'purchaseOrder' }),
    },
    products: { label: 'Products', type: 'table', children: ProductsComponent.definitionMap },
    expenses: { label: 'Charges', type: 'table', children: ExpensesComponent.definitionMap },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'purchaseOrder' },
    billToAddress: { label: 'Bill To Address', type: 'address', children: AddressComponent.definitionMap },
    chargesSubtotal: { label: 'Expenses Subtotal', type: 'string' },
    vendorSo: { label: 'Vendor SO', type: 'string' },
    notes: { label: 'Notes', type: 'list', children: { text: { label: 'Text', type: 'string' } } },
    phoneNumber: { label: 'Phone Number', type: 'string' },
    email: { label: 'Email', type: 'string' },
    businessAddress: { label: 'Business Address', type: 'address', children: AddressComponent.definitionMap },
  },
};
